import { supabase } from './supabase'
import { sendInvitationEmail, sendPrTeamInvitationEmail } from './gmailService'

// Generate a secure temporary password
export const generateTempPassword = (): string => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  const symbols = '!@#$%*'
  let password = ''
  
  // Ensure at least one uppercase, one lowercase, one number, one symbol
  password += 'ABCDEFGHJKMNPQRSTUVWXYZ'[Math.floor(Math.random() * 23)]
  password += 'abcdefghijkmnpqrstuvwxyz'[Math.floor(Math.random() * 23)]
  password += '23456789'[Math.floor(Math.random() * 8)]
  password += symbols[Math.floor(Math.random() * symbols.length)]
  
  // Fill remaining 8 characters
  for (let i = 4; i < 12; i++) {
    password += chars[Math.floor(Math.random() * chars.length)]
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('')
}

// Generate a unique invitation token
export const generateInvitationToken = (): string => {
  return crypto.randomUUID() + '-' + Date.now().toString(36)
}

// Hash password (simple hash for demo - in production use bcrypt)
export const hashPassword = async (password: string): Promise<string> => {
  // For now, we'll store the temp password directly and implement proper hashing later
  // In production, use bcrypt or similar
  return password
}

// Create user invitation
export const createUserInvitation = async (
  journalistEmail: string,
  journalistName: string,
  invitedBy: string
): Promise<{ tempPassword: string; invitationToken: string } | null> => {
  try {
    // Check if user already exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, invitation_status')
      .ilike('email', journalistEmail)
      .single()
    
    if (existingUser) {
      throw new Error('User already has an invitation or account')
    }
    
    // Generate credentials
    const tempPassword = generateTempPassword()
    const invitationToken = generateInvitationToken()
    const passwordHash = await hashPassword(tempPassword)
    
    // Create user record
    const { data, error } = await supabase
      .from('users')
      .insert({
        email: journalistEmail,
        name: journalistName,
        role: 'journalist',
        temp_password: tempPassword,
        password_hash: passwordHash,
        must_change_password: true,
        invitation_status: 'pending'
      })
      .select()
      .single()
    
    if (error) throw error
    
    return { tempPassword, invitationToken }
  } catch (error) {
    console.error('Error creating user invitation:', error)
    throw error
  }
}

// Create PR team member invitation
export const createPrTeamInvitation = async (
  email: string,
  name: string,
  role: string,
  invitedBy: string
): Promise<{ tempPassword: string; invitationToken: string } | null> => {
  try {
    // Check if user already exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, invitation_status')
      .ilike('email', email)
      .single()
    
    if (existingUser) {
      throw new Error('User already has an invitation or account')
    }
    
    // Generate credentials
    const tempPassword = generateTempPassword()
    const invitationToken = generateInvitationToken()
    const passwordHash = await hashPassword(tempPassword)
    
    // Create user record with PR role
    const { data, error } = await supabase
      .from('users')
      .insert({
        email: email,
        name: name,
        role: role || 'pr',
        temp_password: tempPassword,
        password_hash: passwordHash,
        must_change_password: true,
        invitation_status: 'pending'
      })
      .select()
      .single()
    
    if (error) throw error
    
    return { tempPassword, invitationToken }
  } catch (error) {
    console.error('Error creating PR team invitation:', error)
    throw error
  }
}

// Check if journalist has been invited
export const checkInvitationStatus = async (email: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('invitation_status')
      .ilike('email', email)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error // PGRST116 is "not found"
    
    return data?.invitation_status || null
  } catch (error) {
    console.error('Error checking invitation status:', error)
    return null
  }
}

// Send invitation to PR team member (create account + send email)
export const invitePrTeamMember = async (
  email: string,
  name: string,
  role: string,
  senderEmail: string,
  senderName: string
): Promise<boolean> => {
  try {
    console.log('🔄 Starting PR team member invitation for:', email);
    
    // Create the user account
    console.log('📝 Creating user account...');
    const result = await createPrTeamInvitation(email, name, role, senderEmail)
    if (!result) {
      console.error('❌ User account creation failed - no result returned');
      return false;
    }
    
    console.log('✅ User account created, temp password:', result.tempPassword);
    
    // Send the email using simple PR Team template
    console.log('📧 Sending PR Team email notification...');
    const emailSent = await sendPrTeamInvitationEmail(
      email,
      name,
      result.tempPassword,
      result.invitationToken,
      senderEmail,
      senderName
    )
    
    if (!emailSent) {
      console.warn('⚠️ Invitation email failed to send, but user account was created')
      return false;
    }
    
    console.log('✅ PR team member invitation completed successfully');
    return true;
  } catch (error) {
    console.error('❌ Error inviting PR team member:', error)
    console.error('❌ Error details:', error.message);
    return false
  }
}

// Send invitation email using Gmail API (imported above)

// Create password reset for existing user
export const createPasswordReset = async (
  userEmail: string,
  userName: string,
  invitedBy: string
): Promise<{ tempPassword: string; invitationToken: string } | null> => {
  try {
    // Check if user exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, name')
      .ilike('email', userEmail)
      .single()
    
    if (!existingUser) {
      throw new Error('User not found')
    }
    
    // Generate new credentials
    const tempPassword = generateTempPassword()
    const invitationToken = generateInvitationToken()
    const passwordHash = await hashPassword(tempPassword)
    
    // Update existing user record with new temp password
    const { data, error } = await supabase
      .from('users')
      .update({
        temp_password: tempPassword,
        password_hash: passwordHash,
        must_change_password: true,
        invitation_status: 'pending'
      })
      .ilike('email', userEmail)
      .select()
      .single()
    
    if (error) throw error
    
    return { tempPassword, invitationToken }
  } catch (error) {
    console.error('Error creating password reset:', error)
    throw error
  }
}

// Send password reset to existing user
export const sendPasswordReset = async (
  userEmail: string,
  userName: string,
  invitedBy: string,
  senderEmail: string,
  senderName: string
): Promise<boolean> => {
  try {
    console.log('🔄 Starting password reset for:', userEmail);
    
    // Create the password reset
    console.log('📝 Creating password reset...');
    const result = await createPasswordReset(userEmail, userName, invitedBy)
    if (!result) {
      console.error('❌ Password reset creation failed - no result returned');
      return false;
    }
    
    console.log('✅ Password reset created, temp password:', result.tempPassword);
    
    // Send the email using simple PR Team template  
    console.log('📧 Sending PR Team password reset email...');
    const emailSent = await sendPrTeamInvitationEmail(
      userEmail,
      userName,
      result.tempPassword,
      result.invitationToken,
      senderEmail,
      senderName
    )
    
    if (!emailSent) {
      console.warn('⚠️ Password reset email failed to send, but user record was updated')
      return false;
    }
    
    console.log('✅ Password reset completed successfully');
    return true;
  } catch (error) {
    console.error('❌ Error sending password reset:', error)
    console.error('❌ Error details:', error.message);
    return false
  }
}

// Send invitation to journalist  
export const inviteJournalist = async (
  journalistEmail: string,
  journalistName: string,
  invitedBy: string,
  senderEmail: string,
  senderName: string
): Promise<boolean> => {
  try {
    console.log('🔄 Starting journalist invitation for:', journalistEmail);
    
    // Check if user already exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, email')
      .ilike('email', journalistEmail)
      .single()
    
    let tempPassword: string;
    let invitationToken: string;
    
    if (existingUser) {
      console.log('📝 User already exists, creating password reset...');
      // User exists, create a password reset instead
      const resetResult = await createPasswordReset(journalistEmail, journalistName, invitedBy);
      if (!resetResult) {
        console.error('❌ Password reset creation failed');
        return false;
      }
      tempPassword = resetResult.tempPassword;
      invitationToken = resetResult.invitationToken;
      console.log('✅ Password reset created for existing user');
    } else {
      console.log('📝 Creating new journalist user account...');
      // User doesn't exist, create new account
      const result = await createUserInvitation(journalistEmail, journalistName, invitedBy);
      if (!result) {
        console.error('❌ Journalist account creation failed - no result returned');
        return false;
      }
      tempPassword = result.tempPassword;
      invitationToken = result.invitationToken;
      console.log('✅ New journalist account created');
    }
    
    // Send the Fan Expo branded email
    console.log('📧 Sending Fan Expo branded journalist invitation...');
    const emailSent = await sendInvitationEmail(
      journalistEmail,
      journalistName,
      tempPassword,
      invitationToken,
      senderEmail,
      senderName
    )
    
    if (!emailSent) {
      console.warn('⚠️ Journalist invitation email failed to send, but user account was processed')
      return false;
    }
    
    console.log('✅ Journalist invitation completed successfully');
    return true;
  } catch (error) {
    console.error('❌ Error inviting journalist:', error)
    console.error('❌ Error details:', error.message);
    return false
  }
}

// Bulk invite journalists
export const bulkInviteJournalists = async (
  journalists: Array<{ email: string; name: string }>,
  invitedBy: string,
  senderEmail: string,
  senderName: string
): Promise<{ successful: number; failed: Array<{ email: string; error: string }> }> => {
  const results = {
    successful: 0,
    failed: [] as Array<{ email: string; error: string }>
  }
  
  for (const journalist of journalists) {
    try {
      const success = await inviteJournalist(
        journalist.email, 
        journalist.name, 
        invitedBy,
        senderEmail,
        senderName
      )
      if (success) {
        results.successful++
      } else {
        results.failed.push({ email: journalist.email, error: 'Failed to send invitation' })
      }
    } catch (error) {
      results.failed.push({ 
        email: journalist.email, 
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }
  
  return results
}